import Column from './Column';
import ColumnType from './ColumnType';
import { SymbologyValues } from 'app/store/appSettings/AppSettingsState';
import * as CurrencyColumns from './CurrencyColumns';
import * as DateColumns from './/DateColumns';
import * as EnumColumns from './EnumColumns';
import * as IntColumns from './IntColumns';
import * as NumberColumns from './NumberColumns';
import * as StringColumns from './StringColumns';
import * as PriceColumns from './PriceColumns';
import { useSelectAppSettings } from 'app/store/appSettings/selectors';
import { useMemo } from 'react';
import { mapSymbolStreamField } from 'app/store/orders/mapping';
import {
    applyCurrencyToColumn,
    ColumnDefinitionsDictionary,
    ValueType,
} from '@instinet-analytics/semantic-ui-components';
import { columnFilterPropsMap } from 'widgets/blotter/columns/columnFilterProps';
import mapValues from 'lodash/mapValues';

export default interface ColumnPortfolio {
    acct: Column<ColumnType.STRING>;
    adv: Column<ColumnType.INTEGER>;
    advPct: Column<ColumnType.NUMBER>;
    arrivalP: Column<ColumnType.PRICE>;
    arrivalPD: Column<ColumnType.CURRENCYPRICE>;
    arrivalPSlip: Column<ColumnType.NUMBER>;
    arrivalPTime: Column<ColumnType.DATE>;
    avgPx: Column<ColumnType.PRICE>;
    avgPxC: Column<ColumnType.CURRENCYPRICE>;
    basket: Column<ColumnType.STRING>;
    bloom: Column<ColumnType.STRING>;
    client: Column<ColumnType.STRING>;
    clientAlias: Column<ColumnType.STRING>;
    clientId: Column<ColumnType.STRING>;
    clusterId: Column<ColumnType.INTEGER>;
    clusterDescription: Column<ColumnType.STRING>;
    core: Column<ColumnType.STRING>;
    country: Column<ColumnType.STRING>;
    curcy: Column<ColumnType.STRING>;
    cusip: Column<ColumnType.STRING>;
    dcat: Column<ColumnType.DAY_ENUM>;
    deliverToSub: Column<ColumnType.STRING>;
    desk: Column<ColumnType.STRING>;
    deskId: Column<ColumnType.STRING>;
    ex: Column<ColumnType.STRING>;
    exCost: Column<ColumnType.NUMBER>;
    exCostTx: Column<ColumnType.NUMBER>;
    fillAmt: Column<ColumnType.CURRENCY>;
    fillQty: Column<ColumnType.INTEGER>;
    fillT: Column<ColumnType.DATE>;
    handler: Column<ColumnType.STRING>;
    idx: Column<ColumnType.INTEGER>;
    idxEvtTrdCat: Column<ColumnType.STRING>;
    ind: Column<ColumnType.STRING>;
    isin: Column<ColumnType.STRING>;
    isymbol: Column<ColumnType.STRING>;
    limit: Column<ColumnType.PRICE>;
    liq: Column<ColumnType.ENUM>;
    listId: Column<ColumnType.STRING>;
    localSymC: Column<ColumnType.STRING>;
    lp: Column<ColumnType.PRICE>;
    mcap: Column<ColumnType.ENUM>;
    mic: Column<ColumnType.STRING>;
    mktDataMic: Column<ColumnType.STRING>;
    name: Column<ColumnType.STRING>;
    orderID: Column<ColumnType.INTEGER>;
    orderInfo: Column<ColumnType.STRING>;
    ownCat: Column<ColumnType.STRING>;
    owner: Column<ColumnType.STRING>;
    parentId: Column<ColumnType.STRING>;
    parentChild: Column<ColumnType.STRING>;
    pairs: Column<ColumnType.STRING>;
    paused: Column<ColumnType.STRING>;
    pnc: Column<ColumnType.PRICE>;
    pncD: Column<ColumnType.CURRENCYPRICE>;
    qty: Column<ColumnType.INTEGER>;
    qtyAmt: Column<ColumnType.CURRENCY>;
    reg: Column<ColumnType.STRING>;
    rem: Column<ColumnType.INTEGER>;
    remAmt: Column<ColumnType.CURRENCY>;
    subInd: Column<ColumnType.STRING>;
    secType: Column<ColumnType.STRING>;
    sector: Column<ColumnType.STRING>;
    sedol: Column<ColumnType.STRING>;
    side: Column<ColumnType.ENUM>;
    state: Column<ColumnType.STRING>;
    stn: Column<ColumnType.STRING>;
    svol: Column<ColumnType.ENUM>;
    sym2: Column<ColumnType.STRING>;
    symbol: Column<ColumnType.STRING>;
    symbolIndex: Column<ColumnType.STRING>;
    tickSize: Column<ColumnType.NUMBER>;
    t0: Column<ColumnType.STRING>;
    t1: Column<ColumnType.STRING>;
    t2: Column<ColumnType.STRING>;
    t3: Column<ColumnType.STRING>;
    t4: Column<ColumnType.STRING>;
    t5: Column<ColumnType.STRING>;
    t6: Column<ColumnType.STRING>;
    t7: Column<ColumnType.STRING>;
    t8: Column<ColumnType.STRING>;
    t9: Column<ColumnType.STRING>;
    t10: Column<ColumnType.STRING>;
    t11: Column<ColumnType.STRING>;
    t12: Column<ColumnType.STRING>;
    t13: Column<ColumnType.STRING>;
    t14: Column<ColumnType.STRING>;
    t15: Column<ColumnType.STRING>;
    t16: Column<ColumnType.STRING>;
    t17: Column<ColumnType.STRING>;
    t18: Column<ColumnType.STRING>;
    t19: Column<ColumnType.STRING>;
    t20: Column<ColumnType.STRING>;
    t21: Column<ColumnType.STRING>;
    t22: Column<ColumnType.STRING>;
    t23: Column<ColumnType.STRING>;
    t24: Column<ColumnType.STRING>;
    timeIn: Column<ColumnType.DATE>;
    timeInT: Column<ColumnType.DATE>;
    timeInD: Column<ColumnType.DATE>;
}

export type BlotterColumn = keyof ColumnPortfolio;
export type BlotterColumns = BlotterColumn[];

export const ClientSideDynamicCols: BlotterColumns = ['symbol', 'exCost'];

export const useColumnPortfolio = () => {
    const { symbology } = useSelectAppSettings();

    return useMemo(() => buildColumnPortfolio(symbology), [symbology]);
};

export const useColumnDefinitions = () => {
    const columnPortfolio = useColumnPortfolio();
    return useMemo(
        () => buildColumnDefinitions(columnPortfolio),
        [columnPortfolio]
    );
};

export function buildColumnPortfolio(
    symbology: SymbologyValues
): ColumnPortfolio {
    const columns: ColumnPortfolio = {
        ...CurrencyColumns,
        ...DateColumns,
        ...EnumColumns,
        ...IntColumns,
        ...NumberColumns,
        ...StringColumns,
        ...PriceColumns,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any;

    columns.symbol.dataField = mapSymbolStreamField(symbology);

    // Ensure ids...
    for (const key in columns) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (columns as any)[key].id = key;
    }

    return columns;
}

export function buildColumnDefinitions(
    columnPortfolio: ColumnPortfolio
): ColumnDefinitionsDictionary<BlotterColumn> {
    const propsMap = columnFilterPropsMap(columnPortfolio);
    return mapValues(columnPortfolio, (c) => {
        const columnId = c.id;
        const col = columnPortfolio[columnId];
        const props = propsMap[columnId];
        return {
            field: col.dataField,
            name: col.name,
            enumMap: props.enumMap,
            precision: props.precision,
            valueType: props.valueType,
            isPercentage: col.isPercentage,
            defaultSortDesc: col.defaultSortDesc,
            sortCompareFunction: col.sortCompareFunction,
            textualQuickSearch: col.textualQuickSearch,
        };
    });
}

export function sanitizeColumns(
    columnPortfolio: ColumnPortfolio,
    columns: BlotterColumns
) {
    return columns.filter((c) => isValidColumn(columnPortfolio, c));
}

export function isValidColumn(
    columnPortfolio: ColumnPortfolio,
    column: BlotterColumn
) {
    return columnPortfolio[column] !== undefined;
}

export function applyCurrency(
    column: Column<ColumnType>,
    currencySymbol?: string
) {
    if (column.colType !== ColumnType.CURRENCY) {
        return column;
    }

    return applyCurrencyToColumn(
        {
            ...column,
            valueType: ValueType.CURRENCY,
        },
        currencySymbol ?? ''
    );
}
